import { useEffect } from 'react';
import { signIn, useSession } from 'next-auth/react';

import { useUserContext } from '../../contexts/UserContext';

import { getCurrentTimeInSeconds } from '@dist-marketplace-frontend/utils';
import { isProtectedRoute } from '../../constants/protectedRoutes';
import { NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE } from '../../helpers/constants';

const RefreshTokenHandler = ({
  setTimeInSecondsBeforeRefreshingSession,
}: {
  setTimeInSecondsBeforeRefreshingSession: (timeInSeconds: number) => void;
}) => {
  const { data: session, status } = useSession();

  const {
    performLogout,
    consentOptions: { functionalConsent },
  } = useUserContext();

  useEffect(() => {
    if (status !== 'authenticated') {
      return;
    }

    handleRefreshSessionOnFocusedTab();

    document.addEventListener(
      'visibilitychange',
      handleRefreshSessionOnFocusedTab,
    );

    return () => {
      document.removeEventListener(
        'visibilitychange',
        handleRefreshSessionOnFocusedTab,
      );

      disableRefreshingSession();
    };

    function handleRefreshSessionOnFocusedTab() {
      switch (document.visibilityState) {
        case 'visible':
          refreshSessionWhenExpired();
          break;
        case 'hidden':
          disableRefreshingSession();
          break;
      }
    }

    function refreshSessionWhenExpired() {
      const currentTimeInSeconds = getCurrentTimeInSeconds();

      if (typeof session?.expiresAt === 'number') {
        const timeRemaining = session.expiresAt - currentTimeInSeconds;

        setTimeInSecondsBeforeRefreshingSession(Math.max(timeRemaining, 0));
      }
    }

    function disableRefreshingSession() {
      setTimeInSecondsBeforeRefreshingSession(0);
    }
  }, [setTimeInSecondsBeforeRefreshingSession, session?.expiresAt, status]);

  useEffect(() => {
    if (status !== 'authenticated') {
      return;
    }

    if (!tokenHasAnError()) {
      return;
    }

    if (isProtectedRoute(location.pathname)) {
      signIn('keycloak');
      return;
    }

    performLogout({ userInitiated: false });

    function tokenHasAnError() {
      return session?.error === NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE;
    }
  }, [performLogout, session?.error, status, functionalConsent]);

  return null;
};

export default RefreshTokenHandler;
